import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PopoverController } from '@ionic/angular';

import { ChatGroup } from '@sc/types';
import { ChatService } from '../../services/chat/chat.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { ChatGroupMenuComponent } from '../../menus/chat-group-menu/chat-group-menu.component';
import { UserService } from '../../services/user/user.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { RoleLimits } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { of, Subscription, switchMap } from 'rxjs';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
  selector: 'sc-chat-groups',
  templateUrl: './chat-groups.component.html',
  styleUrls: ['./chat-groups.component.scss'],
})
export class ChatGroupsComponent implements OnInit, OnDestroy {
  @Output() closeSidebar = new EventEmitter<boolean>();
  @Input() selectChatGroup: (chatGroupID: string) => void;
  activeUser$ = this.userService.activeUser$;
  studioSession$ = this.sessionsService.studioSession$;
  showSettings$ = this.settingsService.studioShowSettings$;
  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;
  chatGroups: Array<ChatGroup> = [];
  subs: Subscription[] = [];

  constructor(
    private userService: UserService,
    private chatService: ChatService,
    private sessionsService: SessionsService,
    private settingsService: SettingsService,
    private popoverController: PopoverController,
    private analyticsService: AnalyticsService,
    private rolesService: RolesService
  ) {}

  /**
   * Initializes the chat groups component
   */
  ngOnInit() {
    this.setupChatGroups();
  }

  /**
   * Selects one active chat group
   *
   * @param chatGroup
   */
  async select(chatGroup: ChatGroup) {
    this.selectChatGroup(chatGroup.chatGroupID);
    await this.analyticsService.track('selected chat group', {
      name: chatGroup.name,
      chatGroupID: chatGroup.chatGroupID,
    });
  }

  /**
   * Opens the new chat group menu
   *
   * @param click
   */
  async openNewChatGroupMenu(click: MouseEvent) {
    const popover = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: ChatGroupMenuComponent,
      componentProps: { sessionID: this.studioSession$.value.sessionID },
      cssClass: 'chat-group-menu',
      event: click,
    });

    await popover.present();
    await this.analyticsService.track('clicked create chat group');
  }


  /**
   * Prevents flickering/flashing in the list of chat groups
   *
   * @param index -number
   * @param chatGroup - ChatGroup
   * @returns - string
   */
  trackByChatGroup(index: number, chatGroup: ChatGroup) {
    return chatGroup.chatGroupID;
  }

  /**
   * Sets up the chat groups
   */
  setupChatGroups() {
    const sub = this.chatService
      .getParticipantChatGroups(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
      .pipe(
        switchMap((chatGroups) => {
          if (!chatGroups || !chatGroups.length) return of([]);
          this.chatGroups = chatGroups;
          return this.chatService.unreadChatGroups$;
        })
      )
      .subscribe((unreadChatGroups) => {
        this.chatGroups = this.chatGroups.map((chatGroup) => ({
          ...chatGroup,
          unread: unreadChatGroups.find((unreadChatGroup) => unreadChatGroup === chatGroup.chatGroupID) ? true : false,
        }));
      });

    this.subs.push(sub);
  }

  /**
   * Destroys the component
   */
  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }
}
